<template>
    <v-dialog v-model="internalShow" max-width="560" persistent>
        <v-card>
            <v-card-title>{{ $t('routePlanning.dialogs.historico.saveTitle') }}</v-card-title>
            <v-card-text>
                <v-progress-linear v-if="loading" indeterminate class="mb-4" />
                <v-text-field
                    v-model="titulo"
                    :label="$t('routePlanning.dialogs.historico.titleField')"
                    :disabled="loading || saving"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                />
                <div class="text-caption text--secondary mb-1">
                    {{ $t('routePlanning.dialogs.historico.saveSummary') }}
                </div>
                <ul class="text-body-2 mb-0 pl-4">
                    <li>{{ $t('routePlanning.fields.destination') }}: {{ destinationLabel }}</li>
                    <li>{{ $t('routePlanning.routeTable.title') }}: {{ summary.qtd_rotas_operacionais }}</li>
                    <li>{{ $t('routePlanning.sidebar.costTotalKm') }}: {{ summary.km_total }}</li>
                    <li v-if="summary.custo_total != null">
                        {{ $t('routePlanning.sidebar.costTotal') }}: {{ summary.custo_total }}
                    </li>
                    <li v-if="summary.qtd_motoristas != null">
                        {{ $t('routePlanning.drivers.uniqueTotal') }}: {{ summary.qtd_motoristas }}
                    </li>
                    <li v-if="summary.custo_motoristas_total != null">
                        {{ $t('routePlanning.drivers.costTotal') }}: {{ summary.custo_motoristas_total }}
                    </li>
                </ul>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn text color="primary" :disabled="saving" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    dark
                    :loading="saving"
                    :disabled="loading || !tituloTrimmed"
                    @click="save"
                >
                    {{ $t('button.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import {
    buildHistoricoPayload,
    computeHistoricoTotals,
    buildDefaultHistoricoTitulo,
    resolveHistoricoAgrupamento,
    newHistoricoSessaoId,
    TIPO_EVENTO_HISTORICO,
} from '@/utils/historicoRoteirizacaoSnapshot';

const EMPTY_SUMMARY = {
    qtd_rotas_operacionais: 0,
    km_total: '0.00 km',
    custo_total: null,
    qtd_motoristas: null,
    custo_motoristas_total: null,
};

export default {
    name: 'RoteirizacaoHistoricoSaveDialog',
    inject: {
        roteirizacaoHistoricoContext: { default: null },
    },
    props: {
        value: { type: Boolean, default: false },
        idUsuario: { type: [Number, String], default: null },
    },
    data() {
        return {
            titulo: '',
            loading: false,
            saving: false,
            agrupamento: { id_unidade: null, nome: '' },
            proximaVersao: 1,
        };
    },
    computed: {
        internalShow: {
            get() { return this.value; },
            set(v) { this.$emit('input', v); },
        },
        tituloTrimmed() {
            return this.titulo != null ? String(this.titulo).trim() : '';
        },
        destinationLabel() {
            const vm = this.resolveVm();
            const d = vm && vm.destination;
            return d && d.nome_cerca ? d.nome_cerca : '—';
        },
        summary() {
            const vm = this.resolveVm();
            if (!vm) return EMPTY_SUMMARY;
            // Só os totais: montar o payload inteiro aqui (computed reativo) custava centenas de MB.
            const payload = computeHistoricoTotals(vm);
            const money = (value) => (value != null
                ? Number(value).toLocaleString(
                    this.$i18n.locale === 'es-MX' ? 'es-MX' : 'pt-BR',
                    { style: 'currency', currency: 'BRL' },
                )
                : null);
            return {
                qtd_motoristas: payload.qtd_motoristas != null ? payload.qtd_motoristas : null,
                custo_motoristas_total: money(payload.custo_motoristas_total),
                qtd_rotas_operacionais: payload.qtd_rotas_operacionais,
                km_total: `${Number(payload.km_total).toFixed(2)} km`,
                custo_total: payload.custo_total != null
                    ? Number(payload.custo_total).toLocaleString(
                        this.$i18n.locale === 'es-MX' ? 'es-MX' : 'pt-BR',
                        { style: 'currency', currency: 'BRL' },
                    )
                    : null,
            };
        },
    },
    watch: {
        value: {
            handler(open) {
                if (open) this.prepare();
            },
            immediate: true,
        },
    },
    methods: {
        resolveVm() {
            return typeof this.roteirizacaoHistoricoContext === 'function'
                ? this.roteirizacaoHistoricoContext()
                : null;
        },
        close() {
            this.internalShow = false;
        },
        async prepare() {
            const vm = this.resolveVm();
            if (!vm) return;
            this.loading = true;
            this.titulo = '';
            try {
                if (typeof vm.ensureDadosValidacaoCentroUnidades === 'function') {
                    await vm.ensureDadosValidacaoCentroUnidades();
                }
                this.agrupamento = resolveHistoricoAgrupamento(
                    vm.destination,
                    vm.centerUnits,
                );
                const dest = vm.destination || {};
                const params = {
                    id_cerca_destino: dest.id_cerca,
                };
                if (this.agrupamento.id_unidade != null) {
                    params.id_unidade = this.agrupamento.id_unidade;
                }
                const res = await apiController.request(
                    apiController.endpoints.getProximaversaohistoricoroteirizacao,
                    { query: params, silent: true },
                );
                const versao = res.data && res.data.proxima_versao != null
                    ? Number(res.data.proxima_versao)
                    : 1;
                this.proximaVersao = versao;
                const built = await buildDefaultHistoricoTitulo(vm, versao);
                this.titulo = built.titulo;
                this.agrupamento = built.agrupamento;
            } catch (e) {
                const built = await buildDefaultHistoricoTitulo(vm, 1);
                this.titulo = built.titulo;
                this.agrupamento = built.agrupamento;
            } finally {
                this.loading = false;
            }
        },
        async save() {
            if (!this.tituloTrimmed) return;
            const vm = this.resolveVm();
            if (!vm) return;
            this.saving = true;
            try {
                if (!vm.historicoSessaoId) {
                    vm.historicoSessaoId = newHistoricoSessaoId();
                }
                const payload = buildHistoricoPayload(vm, {
                    titulo: this.tituloTrimmed,
                    idUsuario: this.idUsuario,
                    agrupamento: this.agrupamento,
                    tipoEvento: TIPO_EVENTO_HISTORICO.MANUAL,
                    idSessao: vm.historicoSessaoId,
                });
                const res = await apiController.request(
                    apiController.endpoints.insertHistoricoroteirizacao,
                    { data: payload, silent: true },
                );
                if (res.data && res.data.erro) {
                    EventBus.$emit('show-alert', {
                        tipo: 'error',
                        mensagem: res.data.mensagem || this.$t('routePlanning.dialogs.historico.saveError'),
                    });
                    return;
                }
                EventBus.$emit('show-alert', {
                    tipo: 'success',
                    mensagem: this.$t('routePlanning.dialogs.historico.saveSuccess'),
                });
                this.$emit('saved', res.data);
                this.close();
            } catch (e) {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: this.$t('routePlanning.dialogs.historico.saveError'),
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
